import type { ImageAspectRatio } from './chat';

export type DesignExtensionLane = 'fashion';

export type DesignExtensionPreset =
  | 'garment_variants'
  | 'outfit_style_extensions'
  | 'person_multiview';

export type LegacyDesignExtensionPreset = 'three_view_consistency';

export type DesignExtensionPresetInput = DesignExtensionPreset | LegacyDesignExtensionPreset;

export function normalizeDesignExtensionPreset(value: string | null | undefined): DesignExtensionPreset | null {
  const preset = value?.trim();
  if (!preset) {
    return null;
  }

  if (preset === 'three_view_consistency') {
    return 'person_multiview';
  }

  if (
    preset === 'garment_variants'
    || preset === 'outfit_style_extensions'
    || preset === 'person_multiview'
  ) {
    return preset;
  }

  return null;
}

export type DesignExtensionThreeViewFocus = 'portrait' | 'full_body' | 'product';

export type DesignExtensionSourceRole =
  | 'primary'
  | 'subject'
  | 'style_reference'
  | 'model_reference'
  | 'background_reference';

export type DesignExtensionAspectRatio = ImageAspectRatio;

export type DesignExtensionOutputKind = 'image' | 'sketch';

export type DesignExtensionIntensity = 'subtle' | 'balanced' | 'bold';

export const DESIGN_EXTENSION_IMAGE_RESOLUTION = '2K' as const;

export type DesignExtensionImageResolution = '512' | '1K' | typeof DESIGN_EXTENSION_IMAGE_RESOLUTION | '4K' | '720p' | '1080p';

export function getDesignExtensionImageResolution(
  model: string,
  preset?: DesignExtensionPresetInput,
  focus?: DesignExtensionThreeViewFocus,
): DesignExtensionImageResolution {
  return model === 'soul-2' && normalizeDesignExtensionPreset(preset) === 'person_multiview' && focus === 'portrait'
    ? '1080p'
    : DESIGN_EXTENSION_IMAGE_RESOLUTION;
}

export interface DesignExtensionSourceItem {
  role: DesignExtensionSourceRole;
  image_url: string;
  clip_id?: string;
  label?: string;
}

export interface DesignExtensionGenerateRequest {
  project_id: string;
  canvas_node_id?: string;
  lane: DesignExtensionLane;
  preset: DesignExtensionPreset;
  sources: DesignExtensionSourceItem[];
  board_count: number;
  aspect_ratio: DesignExtensionAspectRatio;
  output_kind?: DesignExtensionOutputKind;
  intensity: DesignExtensionIntensity;
  creative_note?: string;
  person_multiview_focus?: DesignExtensionThreeViewFocus;
  three_view_focus?: DesignExtensionThreeViewFocus;
  image_model: string;
  image_resolution?: DesignExtensionImageResolution;
  selected_character_model_id?: string;
}

export interface DesignExtensionToolbarSubmitRequest {
  clipIds: string[];
  preset: DesignExtensionPreset;
  boardCount: number;
  aspectRatio: DesignExtensionAspectRatio;
  outputKind: DesignExtensionOutputKind;
  intensity: DesignExtensionIntensity;
  personMultiviewFocus?: DesignExtensionThreeViewFocus;
  threeViewFocus?: DesignExtensionThreeViewFocus;
}

export interface DesignExtensionGenerateResult {
  task_id: string;
  capability: 'image_generation';
  status: 'queued' | 'processing';
  provider?: string;
  queue_position?: number;
  estimated_wait_sec?: number;
}
